import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, FormHelperText, InputLabel, Select, MenuItem,
  CircularProgress, Alert, AlertTitle, Snackbar, Tooltip, Switch, FormControlLabel, Tabs, Tab,
} from '@mui/material'
import { Add, Edit, Delete, AutoAwesome, PlayArrow, Lock, LibraryBooks } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { kbApi, tenantApi, analysisApi, scannerApi } from '../services/api'
import { byofApi, ByofFramework } from '../services/api'
import { RootState } from '../store'
import { useEntitlements } from '../hooks/useEntitlements'
import FrameworksPanel from '../components/FrameworksPanel'
import BundleTeaser from '../components/BundleTeaser'
import { BUNDLE_COPY } from '../components/bundleCopy'
import useCanContactVendor from '../hooks/useCanContactVendor'
import BundleBadge from '../components/BundleBadge'

const SEVERITIES = ['critical', 'high', 'medium', 'low']
const DIMENSIONS = [
  { value: 'prompt_engineering', label: 'Prompt Engineering' },
  { value: 'agent_design', label: 'Agent Design' },
  { value: 'memory_architecture', label: 'Memory Architecture' },
  { value: 'orchestration_soundness', label: 'Orchestration' },
  { value: 'observability_tracing', label: 'Observability' },
  { value: 'governance_alignment', label: 'Governance' },
]

const getSeverityColor = (s: string) => {
  const map: any = { critical: 'error', high: 'warning', medium: 'info', low: 'success' }
  return map[s] || 'default'
}

const SEVERITY_PENALTY: Record<string, number> = {
  critical: 0.8, high: 0.6, medium: 0.4, low: 0.2,
}
const severityPenalty = (s: string) => SEVERITY_PENALTY[s] ?? 0.4

const emptyForm = {
  name: '', description: '', pattern: '', exception_pattern: '',
  severity: 'medium', dimension: 'governance_alignment',
  category: 'rate_limiting_quotas', remediation: '',
  framework: 'general',
  lifecycle_state: 'active',
  // Opt-OUT default — new tenant-authored rules surface to ARIAS for review
  // by default. The platform-admin "KB Review" dashboard (FEATURE_ROADMAP §11/§18
  // Phase D) lets us evaluate and either adopt globally or reject. Tenants who
  // prefer to keep a rule private flip this off explicitly.
  propose_to_platform: true,
}

// The canonical 27 finding categories, grouped by their dimension.
// Mirrors services/shared/taxonomy.py, which is the authority.
//
// Category is not a display label — it is the routing key. Compliance Posture
// decides which framework controls a rule's findings are evidence for by
// looking up its category, so a value outside this list routes nowhere.
//
// This replaces a map that claimed to produce canonical categories and produced
// none: 'prompt_quality' is an analyzer name, 'agent_design' and
// 'memory_architecture' are dimensions rather than categories, and
// 'orchestration' / 'observability' / 'governance' are not values the taxonomy
// has ever contained. Every tenant-authored rule was getting one of them.
const CATEGORIES_BY_DIMENSION: Record<string, { value: string; label: string }[]> = {
  prompt_engineering: [
    { value: 'prompt_injection_defense',   label: 'Prompt injection defense' },
    { value: 'system_prompt_construction', label: 'System prompt construction' },
    { value: 'input_validation',           label: 'Input validation' },
    { value: 'output_format_parsing',      label: 'Output format & parsing' },
    { value: 'refusal_scope',              label: 'Refusal scope' },
  ],
  agent_design: [
    { value: 'tool_governance',      label: 'Tool governance' },
    { value: 'error_handling',       label: 'Error handling' },
    { value: 'model_configuration',  label: 'Model configuration' },
    { value: 'autonomy_controls',    label: 'Autonomy controls' },
    { value: 'code_execution_safety', label: 'Code execution safety' },
  ],
  memory_architecture: [
    { value: 'context_window_management', label: 'Context window management' },
    { value: 'cross_session_isolation',   label: 'Cross-session isolation' },
    { value: 'sensitive_state_handling',  label: 'Sensitive state handling' },
  ],
  orchestration_soundness: [
    { value: 'multi_agent_coordination',   label: 'Multi-agent coordination' },
    { value: 'state_management',           label: 'State management' },
    { value: 'subagent_spawning',          label: 'Subagent spawning' },
    { value: 'inter_agent_authentication', label: 'Inter-agent authentication' },
  ],
  observability_tracing: [
    { value: 'tracing_callbacks',      label: 'Tracing & callbacks' },
    { value: 'audit_trail',            label: 'Audit trail' },
    { value: 'logging_coverage',       label: 'Logging coverage' },
    { value: 'metrics_cost_tracking',  label: 'Metrics & cost tracking' },
  ],
  governance_alignment: [
    { value: 'rate_limiting_quotas',  label: 'Rate limiting & quotas' },
    { value: 'human_in_the_loop',     label: 'Human in the loop' },
    { value: 'policy_alignment',      label: 'Policy alignment' },
    { value: 'regulatory_compliance', label: 'Regulatory compliance' },
    { value: 'secrets_credentials',   label: 'Secrets & credentials' },
    { value: 'access_control',        label: 'Access control' },
  ],
}

// First category of a dimension, used only as the initial selection. The author
// can change it, which matters: picking a dimension's first category for them is
// how server-side detectors used to mis-file findings before the taxonomy work.
const defaultCategoryFor = (dimension: string): string =>
  CATEGORIES_BY_DIMENSION[dimension]?.[0]?.value || 'policy_alignment'

const LIFECYCLE_OPTIONS = [
  { value: 'active',  label: 'Active — fires on real scans' },
  { value: 'draft',   label: 'Draft — only runs in dry-run, not on real scans' },
  { value: 'testing', label: 'Testing — same as draft, separate label for iteration' },
  { value: 'retired', label: 'Retired — soft-deleted, hidden from default views' },
]

const LIFECYCLE_COLORS: Record<string, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success', draft: 'warning', testing: 'info', retired: 'default',
}

const CustomRulesPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id

  const { has, loading: entitlementsLoading } = useEntitlements()
  // Frameworks are an Enterprise entitlement; the tab is only offered when the
  // tenant has it. The panel asks the server regardless — that is the
  // authority, and this only decides whether to advertise the surface.
  const hasFrameworks = has('custom_framework')
  // Defaults to `frameworks`. It defaulted to `rules`, which opened the page
  // on a standalone rule form — the step rather than the process, and the
  // reason the form read as the product. `?tab=rules` still lands there, so
  // links into the rules list keep working.
  const [tab, setTab] = useState<'rules' | 'frameworks'>(
    () => (new URLSearchParams(window.location.search).get('tab') === 'rules'
      ? 'rules' : 'frameworks'),
  )

  // Frameworks this rule should belong to. Held separately from `form`
  // because membership is written through the analysis service after the rule
  // itself is saved in the knowledge base — two services, one intent.
  const [frameworkOptions, setFrameworkOptions] = useState<ByofFramework[]>([])
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([])
  const [originalFrameworks, setOriginalFrameworks] = useState<string[]>([])
  const [projections, setProjections] = useState<string[]>([])

  const [rules, setRules] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [canCreateRules, setCanCreateRules] = useState(true)
  // Refused for want of ARIAS Evidence, which is a different thing from a
  // tier that does not include custom rules.
  const canContactVendor = useCanContactVendor()
  // The LIST endpoint stays open on purpose, so refusal cannot be read from a
  // 404 here. `useEntitlements` already defaults to not-entitled while loading
  // and on failure, matching the backend's fail-closed posture — but a teaser
  // that flashes before the answer arrives would be worse than a beat's delay,
  // hence the explicit loading guard.
  const refused = !entitlementsLoading && !has('custom_framework')
  const [maxCustomRules, setMaxCustomRules] = useState<number | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<any>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [assisting, setAssisting] = useState(false)
  const [testCode, setTestCode] = useState('')
  const [testMatches, setTestMatches] = useState<{ matched: boolean; count: number; highlights: string[] } | null>(null)
  const [similarRules, setSimilarRules] = useState<Array<{ rule_id: string; name: string; similarity: number; is_global: boolean; description_preview: string }>>([])
  const [groundedFrameworks, setGroundedFrameworks] = useState<string[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [templates, setTemplates] = useState<Array<any>>([])
  const [templateCategories, setTemplateCategories] = useState<Array<{ id: string; label: string }>>([])
  const [templatesLoaded, setTemplatesLoaded] = useState(false)
  const [activeCategory, setActiveCategory] = useState<string>('all')
  // AI Assist extended response (Beats 2-5)
  const [assistExamples, setAssistExamples] = useState<{ matching: string; non_matching: string } | null>(null)
  const [assistExplanation, setAssistExplanation] = useState<string>('')
  const [assistVariants, setAssistVariants] = useState<{ strict: any; balanced: any; loose: any } | null>(null)
  const [assistQuality, setAssistQuality] = useState<{ specificity?: number; fp_likelihood?: number; concerns?: string[] } | null>(null)
  const [activeVariant, setActiveVariant] = useState<'strict' | 'balanced' | 'loose'>('balanced')
  const [dryRunning, setDryRunning] = useState(false)
  const [dryRunResult, setDryRunResult] = useState<{
    would_fire_on: number;
    total_agents_evaluated: number;
    total_scans_evaluated: number;
    sample_matches: Array<{ scan_session_id: string; agent_name: string; framework: string; file_path: string; severity: string; timestamp: string }>;
  } | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })

  useEffect(() => { fetchRules(); fetchTenantFeatures() }, [tenantId])
  useEffect(() => { loadFrameworkOptions() }, [hasFrameworks, tenantId])

  const fetchTenantFeatures = async () => {
    if (!tenantId) return
    try {
      const res = await tenantApi.getTenant(tenantId)
      const config = res.data?.config || {}
      const maxRules = config.max_custom_rules ?? 0
      setMaxCustomRules(maxRules)
      setCanCreateRules(maxRules > 0)
    } catch {}
  }

  const fetchRules = async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      const res = await kbApi.getRules({ tenant_id: tenantId, active_only: false, limit: 500 })
      const data = res.data.rules || res.data || []
      // Filter to only this tenant's custom rules
      setRules(data.filter((r: any) => r.tenant_id === tenantId))
    } catch (e) {
      console.error('Failed to fetch rules:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!form.name || !form.pattern) return
    setSaving(true)
    try {
      // The author picks the category; it is the routing key, not a label.
      const category = form.category || defaultCategoryFor(form.dimension)
      const ruleData = {
        id: editingRule?.id || `CUSTOM-${tenantId?.substring(0, 8)}-${Date.now()}`,
        name: form.name,
        description: form.description,
        pattern: form.pattern,
        severity: form.severity,
        framework: form.framework,
        category,
        remediation: form.remediation,
        tags: ['custom_rule', form.dimension],
        version: '1.0.0',
        active: true,
        lifecycle_state: form.lifecycle_state || 'active',
        domain: 'best_practices',
        type: 'anti_pattern',
        detection: {
          code_signatures: [form.pattern],
          exception_patterns: form.exception_pattern ? [form.exception_pattern] : [],
        },
        relationships: {
          causes: [], caused_by: [], violates: [], resolved_by: [], resolves: [],
          evidence_rules: [],
          maturity_impact: [
            { dimension: form.dimension, score_penalty: severityPenalty(form.severity),
              rationale: form.description }
          ],
        },
        remediation_detail: { effort: 'medium', code_example_bad: '', code_example_good: '' },
        // adoption_status follows the user's "Propose to platform" toggle. Phase D
        // surfaces 'proposed_for_global' rules in the platform-admin convergence dashboard.
        metadata_ext: {
          source: 'custom_rule',
          validated_by: 'tenant_admin',
          status: 'approved',
          adoption_status: form.propose_to_platform ? 'proposed_for_global' : 'tenant_only',
          proposed_to_platform: !!form.propose_to_platform,
        },
      }

      let ruleId = editingRule?.id
      if (editingRule) {
        await kbApi.updateRule(editingRule.id, ruleData)
        setSnackbar({ open: true, message: 'Rule updated', severity: 'success' })
      } else {
        const created = await kbApi.createRule(ruleData, { params: { tenant_id: tenantId } })
        ruleId = created?.data?.id || ruleData.id
        setSnackbar({ open: true, message: 'Custom rule created', severity: 'success' })
      }

      // Membership is applied after the rule exists, and only the difference is
      // sent. Detaching a framework the author cleared matters as much as
      // attaching a new one — a rule silently left in a framework it was
      // removed from keeps satisfying a control nobody thinks it covers.
      if (ruleId && hasFrameworks) {
        const added = selectedFrameworks.filter((k) => !originalFrameworks.includes(k))
        const dropped = originalFrameworks.filter((k) => !selectedFrameworks.includes(k))
        const notes: string[] = []
        for (const key of added) {
          try {
            const resp = await byofApi.addRule(key, ruleId)
            const projection = resp.data?.evidence_projection
            if (projection?.explanation) notes.push(projection.explanation)
          } catch {
            notes.push(`Could not add this rule to ${key}.`)
          }
        }
        for (const key of dropped) {
          try { await byofApi.removeRule(key, ruleId) } catch { /* reported below */ }
        }
        setProjections(notes)
      }

      setDialogOpen(false)
      setEditingRule(null)
      setForm(emptyForm)
      fetchRules()
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Failed to save rule', severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (rule: any) => {
    const dim = (rule.tags || []).find((t: string) => DIMENSIONS.some(d => d.value === t)) || 'governance_alignment'
    setForm({
      name: rule.name || '',
      description: rule.description || '',
      pattern: rule.pattern || '',
      exception_pattern: rule.detection?.exception_patterns?.[0] || '',
      severity: rule.severity || 'medium',
      dimension: dim,
      // Keep the stored category when it is still one of this dimension's
      // canonical values; otherwise fall back rather than show a blank select.
      // Rules authored before the category fix carry values like 'governance'
      // that are not in the list at all.
      category: (CATEGORIES_BY_DIMENSION[dim] || []).some(c => c.value === rule.category)
        ? rule.category
        : defaultCategoryFor(dim),
      remediation: rule.remediation || '',
      framework: rule.framework || 'general',
      lifecycle_state: rule.lifecycle_state || 'active',
      propose_to_platform: !!(rule.metadata_ext?.proposed_to_platform || rule.metadata_ext?.adoption_status === 'proposed_for_global'),
    })
    setEditingRule(rule)
    setProjections([])
    setSelectedFrameworks([])
    setOriginalFrameworks([])
    if (hasFrameworks) {
      byofApi.frameworksForRule(rule.id)
        .then((resp) => {
          const keys = (resp.data.frameworks || []).map((f) => f.key)
          setSelectedFrameworks(keys)
          setOriginalFrameworks(keys)
        })
        .catch(() => { /* the selector simply shows none */ })
    }
    setDialogOpen(true)
  }

  const openNewRuleDialog = () => {
    setForm(emptyForm)
    setEditingRule(null)
    setSelectedFrameworks([])
    setOriginalFrameworks([])
    setProjections([])
    // Refreshed at the moment the selector is about to be used, so a framework
    // created in another tab — or another window — is always offered.
    loadFrameworkOptions()
    setDialogOpen(true)
  }

  const loadFrameworkOptions = async () => {
    if (!hasFrameworks) return
    try {
      const resp = await byofApi.list()
      setFrameworkOptions(resp.data.frameworks || [])
    } catch {
      setFrameworkOptions([])
    }
  }

  const toggleRuleActivation = async (rule: any) => {
    // Click on the lifecycle chip in the list flips active ↔ draft.
    // Retired rules don't toggle this way — require explicit edit.
    if (rule.lifecycle_state === 'retired') return
    const next = rule.lifecycle_state === 'active' ? 'draft' : 'active'
    try {
      const payload = {
        ...rule,
        lifecycle_state: next,
        active: true,
        // Required-string fields the API insists on, even when we're only flipping lifecycle:
        name: rule.name,
        description: rule.description || '',
        pattern: rule.pattern,
        severity: rule.severity,
        category: rule.category,
        remediation: rule.remediation || '',
      }
      await kbApi.updateRule(rule.id, payload)
      setSnackbar({ open: true, message: `Rule ${next === 'active' ? 'activated' : 'set to draft'}`, severity: 'success' })
      fetchRules()
    } catch {
      setSnackbar({ open: true, message: 'Failed to update lifecycle', severity: 'error' })
    }
  }

  const handleDelete = async (ruleId: string) => {
    if (!confirm('Delete this custom rule?')) return
    try {
      await kbApi.deleteRule(ruleId)
      setSnackbar({ open: true, message: 'Rule deleted', severity: 'success' })
      fetchRules()
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete rule', severity: 'error' })
    }
  }

  const handleAiAssist = async () => {
    if (!form.description) {
      setSnackbar({ open: true, message: 'Enter a description first — describe what you want to detect', severity: 'error' })
      return
    }
    setAssisting(true)
    setSimilarRules([])
    setGroundedFrameworks([])
    setAssistExamples(null)
    setAssistExplanation('')
    setAssistVariants(null)
    setAssistQuality(null)
    setActiveVariant('balanced')
    try {
      const res = await analysisApi.assistRule({
        description: form.description,
        framework: form.framework,
        severity: form.severity,
        dimension: form.dimension,
      })
      const data = res.data
      setForm(prev => ({
        ...prev,
        pattern: data.pattern || prev.pattern,
        exception_pattern: data.exception_pattern || prev.exception_pattern,
        remediation: data.remediation || prev.remediation,
        name: data.name || prev.name,
      }))
      setSimilarRules(data.existing_similar_rules || [])
      setGroundedFrameworks(data.tenant_frameworks_used || [])
      // Beats 2-5
      if (data.sample_matching_code || data.sample_non_matching_code) {
        setAssistExamples({
          matching: data.sample_matching_code || '',
          non_matching: data.sample_non_matching_code || '',
        })
        // Auto-prime the pattern tester with the matching sample so the user
        // sees the rule fire immediately. The tester is browser-local so this
        // doesn't transmit anything.
        if (data.sample_matching_code) setTestCode(data.sample_matching_code)
      }
      if (data.natural_language_explanation) {
        setAssistExplanation(data.natural_language_explanation)
      }
      if (data.pattern_variants) {
        setAssistVariants(data.pattern_variants)
      }
      if (data.quality) {
        setAssistQuality(data.quality)
      }
      setSnackbar({ open: true, message: 'AI suggestions applied — review and adjust as needed', severity: 'success' })
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'AI assist failed', severity: 'error' })
    } finally {
      setAssisting(false)
    }
  }

  const applyVariant = (variant: 'strict' | 'balanced' | 'loose') => {
    if (!assistVariants) return
    const v = assistVariants[variant]
    if (!v) return
    setActiveVariant(variant)
    setForm(prev => ({
      ...prev,
      pattern: v.pattern || prev.pattern,
      exception_pattern: v.exception_pattern || prev.exception_pattern,
    }))
    setTestMatches(null)
    setDryRunResult(null)
  }

  const runPatternTest = () => {
    if (!form.pattern || !testCode) {
      setTestMatches(null)
      return
    }
    try {
      const regex = new RegExp(form.pattern, 'gim')
      const matches = testCode.match(regex) || []
      setTestMatches({
        matched: matches.length > 0,
        count: matches.length,
        highlights: matches.slice(0, 5),
      })
    } catch {
      setTestMatches({ matched: false, count: 0, highlights: ['Invalid regex pattern'] })
    }
  }

  const loadTemplates = async () => {
    if (templatesLoaded) return
    try {
      const res = await kbApi.getRuleTemplates()
      setTemplates(res.data.templates || [])
      setTemplateCategories(res.data.categories || [])
      setTemplatesLoaded(true)
    } catch (e: any) {
      setSnackbar({ open: true, message: 'Failed to load templates', severity: 'error' })
    }
  }

  const openTemplatePicker = async () => {
    await loadTemplates()
    setPickerOpen(true)
  }

  const handlePickTemplate = (tmpl: any) => {
    setForm({
      name: tmpl.name || '',
      description: tmpl.description || '',
      pattern: tmpl.pattern || '',
      exception_pattern: tmpl.exception_pattern || '',
      severity: tmpl.severity || 'medium',
      dimension: tmpl.dimension || 'governance_alignment',
      // A template's own `category` is picker grouping ('data_handling',
      // 'security'), not a finding category, so it is deliberately not used
      // here — the routing category comes from the dimension's default and the
      // author adjusts it.
      category: defaultCategoryFor(tmpl.dimension || 'governance_alignment'),
      remediation: tmpl.remediation || '',
      framework: tmpl.framework || 'general',
      lifecycle_state: 'draft', // templates land as draft — user reviews + dry-runs first
      // Same opt-OUT default as the from-scratch form. If a tenant customizes
      // a template meaningfully, the result is worth proposing back. If they
      // save the template unmodified, admin review can dedup it as a no-op.
      propose_to_platform: true,
    })
    setEditingRule(null)
    setSimilarRules([])
    setGroundedFrameworks([])
    setTestMatches(null)
    setDryRunResult(null)
    setPickerOpen(false)
    setDialogOpen(true)
  }

  const runDryRunAgainstScans = async () => {
    if (!form.pattern) {
      setSnackbar({ open: true, message: 'Enter a pattern first', severity: 'error' })
      return
    }
    setDryRunning(true)
    setDryRunResult(null)
    try {
      const res = await scannerApi.dryRunRule({
        pattern: form.pattern,
        exception_pattern: form.exception_pattern || undefined,
        name: form.name || 'Draft rule',
        description: form.description,
        severity: form.severity,
        category: form.category || defaultCategoryFor(form.dimension),
        framework: form.framework,
        remediation: form.remediation,
        rule_id: editingRule?.id,
        scan_limit: 5,
      })
      setDryRunResult(res.data)
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.detail || 'Dry-run failed — check your pattern syntax',
        severity: 'error',
      })
    } finally {
      setDryRunning(false)
    }
  }

  const generateAutoName = () => {
    const dimLabel = DIMENSIONS.find(d => d.value === form.dimension)?.label || 'Governance'
    const sevLabel = form.severity.charAt(0).toUpperCase() + form.severity.slice(1)
    const fwLabel = form.framework === 'general' ? '' : ` in ${form.framework.charAt(0).toUpperCase() + form.framework.slice(1)}`
    // Extract a short descriptor from description
    const desc = form.description.trim()
    if (!desc) return
    // Take first sentence or first 60 chars
    const shortDesc = desc.includes('.') ? desc.split('.')[0] : desc.substring(0, 60)
    // Title case
    const titleCase = shortDesc.replace(/\b\w/g, c => c.toUpperCase())
    setForm(prev => ({ ...prev, name: `${titleCase}${fwLabel}` }))
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h4" component="span">
          Custom Enterprise Frameworks
        </Typography>
        {/* Provenance, shown whether or not the tenant holds it. The page
            described its own process and never said which bundle it came
            with, so a tenant without Evidence read a description of something
            they cannot use before reaching the teaser. */}
        <BundleBadge bundle="ARIAS Evidence" refused={refused} />
        {/* The process, stated in order, because the rule form is a STEP in it
            and used to read as the destination. */}
        <Typography variant="body2" color="text.secondary">
          Import your policy; ARIAS extracts the framework and its detection
          rules; review or add rules here; scans then run against your rules
          alone or alongside ARIAS&rsquo;s, and report as your own compliance
          posture. Rules apply to your scans immediately.
        </Typography>
      </Box>

      {hasFrameworks && (
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {/* Frameworks first and default: the policy import is where the
              process starts, and the rules below are what it produced. */}
          <Tab value="frameworks" label="Frameworks" />
          <Tab value="rules" label="Rules in your frameworks" />
        </Tabs>
      )}

      {hasFrameworks && tab === 'frameworks' && (
        <FrameworksPanel
          onFrameworksChanged={() => {
            // Both, not just the rules. Creating a framework on this tab has
            // to reach the selector on the Rules tab, and refreshing only the
            // rules list left a framework the author had just created missing
            // from the dropdown they were about to use.
            fetchRules()
            loadFrameworkOptions()
          }}
        />
      )}

      {/* `hasFrameworks` is the ENTITLEMENT, so when it is false the tabs are
          hidden and this expression used to evaluate to 'block' — showing the
          standalone rule form to precisely the tenant who does not hold the
          pipeline it belongs to. Both halves now: hidden on the frameworks
          tab, and hidden entirely when refused. */}
      <Box sx={{
        display: refused || (hasFrameworks && tab === 'frameworks') ? 'none' : 'block',
      }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Box />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={!canCreateRules ? 'Custom rules require Business or Enterprise plan' : ''}>
            <span>
              <Button
                variant="outlined"
                startIcon={<LibraryBooks />}
                disabled={!canCreateRules || (maxCustomRules !== null && rules.length >= maxCustomRules)}
                onClick={openTemplatePicker}
              >
                From Template
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!canCreateRules ? 'Custom rules require Business or Enterprise plan' : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<Add />}
                disabled={!canCreateRules || (maxCustomRules !== null && rules.length >= maxCustomRules)}
                onClick={openNewRuleDialog}
              >
                New Rule
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Not entitled to the BYOF pipeline. Custom Rules is a stage in it —
          import a policy, ARIAS extracts the framework and the rules, scans
          run on yours alone or alongside ARIAS's, and the result is a
          compliance posture tile of your own — so it resolves on the same
          entitlement and gets the same teaser. */}
      {refused && (
        <Box sx={{ mb: 2 }}>
          <BundleTeaser
            copy={BUNDLE_COPY.custom_framework}
            canContactVendor={canContactVendor}
          />
        </Box>
      )}

      {/* Tier, not bundle. Kept separate because after Custom Rules joined
          ARIAS Evidence this message would otherwise have told an Enterprise
          tenant to "upgrade your plan" — the plan they already hold. */}
      {!canCreateRules && !refused && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Custom rules are available on Business and Enterprise plans.{' '}
          <a href="/pricing">Upgrade your plan</a> to create custom governance rules.
        </Alert>
      )}

      {canCreateRules && maxCustomRules !== null && maxCustomRules < 999999 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {rules.length} / {maxCustomRules} custom rules used
        </Typography>
      )}

      {rules.length === 0 && canCreateRules ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>No custom rules yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create rules to enforce your organization&apos;s specific standards.
              For example: &quot;All agents must use our internal LLM gateway&quot; or
              &quot;Agents handling PII must have output filtering.&quot;
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button variant="outlined" startIcon={<LibraryBooks />} onClick={openTemplatePicker}>
                Start from a Template
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openNewRuleDialog}>
                Create From Scratch
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : rules.length === 0 && !canCreateRules ? (
        null
      ) : (
        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rule Name</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Dimension</TableCell>
                    <TableCell>Pattern</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule: any) => {
                    const dim = DIMENSIONS.find(d => (rule.tags || []).includes(d.value))
                    return (
                      <TableRow key={rule.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{rule.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{rule.description?.substring(0, 80)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={rule.severity} color={getSeverityColor(rule.severity) as any} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={dim?.label || 'Governance'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {rule.pattern?.substring(0, 40)}{rule.pattern?.length > 40 ? '...' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={
                            rule.lifecycle_state === 'retired'
                              ? 'Retired — edit to reactivate'
                              : `Click to ${rule.lifecycle_state === 'active' ? 'set to draft (stops firing on real scans)' : 'activate (fires on next scan)'}`
                          }>
                            <Chip
                              label={rule.lifecycle_state || (rule.active ? 'active' : 'inactive')}
                              size="small"
                              color={LIFECYCLE_COLORS[rule.lifecycle_state] || 'default'}
                              variant="outlined"
                              onClick={() => toggleRuleActivation(rule)}
                              sx={{ cursor: rule.lifecycle_state === 'retired' ? 'default' : 'pointer', textTransform: 'capitalize' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(rule)}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(rule.id)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Custom Rule' : 'Create Custom Rule'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Step 1: Describe + classify */}
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} required
                placeholder="Describe what you want to detect — e.g., 'Agents that call external APIs without using our approved gateway'"
                value={form.description} onChange={e => { setForm({ ...form, description: e.target.value }); setSimilarRules([]); setGroundedFrameworks([]) }} />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select value={form.severity} label="Severity" onChange={e => setForm({ ...form, severity: e.target.value })}>
                  {SEVERITIES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Dimension</InputLabel>
                <Select
                  value={form.dimension}
                  label="Dimension"
                  onChange={e => setForm({
                    ...form,
                    dimension: e.target.value,
                    category: defaultCategoryFor(e.target.value),
                  })}
                >
                  {DIMENSIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category}
                  label="Category"
                  onChange={e => setForm({ ...form, category: e.target.value })}
                >
                  {(CATEGORIES_BY_DIMENSION[form.dimension] || []).map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Decides which compliance controls this rule's findings count as evidence for
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Framework" value={form.framework}
                onChange={e => setForm({ ...form, framework: e.target.value })}
                helperText="'general' applies to all" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                When this rule fires, the agent's <strong>{DIMENSIONS.find(d => d.value === form.dimension)?.label || 'Governance'}</strong>{' '}
                maturity score drops by <strong>{severityPenalty(form.severity).toFixed(1)}</strong> (out of 10).
                Severity drives the penalty: critical -0.8, high -0.6, medium -0.4, low -0.2.
              </Typography>
            </Grid>

            {/* AI Assist button + data-handling notice */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={assisting ? <CircularProgress size={16} /> : <AutoAwesome />}
                  onClick={handleAiAssist}
                  disabled={assisting || !form.description}
                >
                  {assisting ? 'Generating...' : 'AI Assist — Generate Pattern & Remediation'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Suggests regex pattern, exceptions, and remediation from your description.
                </Typography>
              </Box>
              <Alert severity="info" icon={<Lock fontSize="small" />} sx={{ mt: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
                <strong>Heads up:</strong> AI Assist sends your description text to Anthropic to generate suggestions.
                Do not include code snippets, credentials, customer data, internal identifiers, or other sensitive information.
                Source code and runtime data from your scans are never transmitted by ARIAS.
              </Alert>
              {groundedFrameworks.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Suggestion grounded in your recent scans' frameworks: {groundedFrameworks.join(', ')}.
                </Typography>
              )}
            </Grid>

            {/* Similar existing rules — surfaces when AI Assist finds high-similarity matches */}
            {similarRules.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {similarRules.length} similar rule{similarRules.length === 1 ? '' : 's'} already exist{similarRules.length === 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    Review before creating a duplicate. You may want to refine your description, or — for global rules — request a per-tenant override instead.
                  </Typography>
                  <Box sx={{ maxHeight: 180, overflowY: 'auto', bgcolor: 'background.paper', p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    {similarRules.map((r) => (
                      <Box key={r.rule_id} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.25 }}>
                          <Chip label={`${Math.round(r.similarity * 100)}% match`} size="small" color={r.similarity >= 0.8 ? 'warning' : 'default'} sx={{ height: 18, fontSize: '0.65rem' }} />
                          <Chip label={r.is_global ? 'global' : 'your tenant'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>· {r.rule_id}</Typography>
                        </Box>
                        {r.description_preview && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                            {r.description_preview}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {/* Pattern strictness variants (Beat 4) — only shown when AI returned them */}
            {assistVariants && (assistVariants.strict || assistVariants.balanced || assistVariants.loose) && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Pattern strictness:
                  </Typography>
                  {(['strict', 'balanced', 'loose'] as const).map(v => (
                    <Tooltip key={v} title={assistVariants[v]?.note || ''}>
                      <span>
                        <Button
                          size="small"
                          variant={activeVariant === v ? 'contained' : 'outlined'}
                          disabled={!assistVariants[v]}
                          onClick={() => applyVariant(v)}
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {v}
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {assistVariants[activeVariant]?.note || ''}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Step 2: Pattern + exceptions (auto-filled or manual) */}
            <Grid item xs={12}>
              <TextField fullWidth label="Detection Pattern (regex)" required
                placeholder="e.g., openai\.ChatCompletion|anthropic\.Anthropic"
                value={form.pattern} onChange={e => { setForm({ ...form, pattern: e.target.value }); setTestMatches(null); setDryRunResult(null) }}
                helperText={assistExplanation ? `In plain English: ${assistExplanation}` : "Regular expression to match in code. Finding is generated when this pattern is detected."}
                InputProps={{ sx: { fontFamily: 'monospace' } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Exception Pattern (optional)"
                placeholder="e.g., internal_gateway|llm_proxy"
                value={form.exception_pattern} onChange={e => setForm({ ...form, exception_pattern: e.target.value })}
                helperText="If this pattern is also present, the finding is suppressed."
                InputProps={{ sx: { fontFamily: 'monospace' } }} />
            </Grid>

            {/* Quality self-assessment from Claude (Beat 5) */}
            {assistQuality && (assistQuality.specificity != null || (assistQuality.concerns?.length ?? 0) > 0) && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: (assistQuality.concerns?.length ?? 0) > 0 ? 1 : 0 }}>
                    {assistQuality.specificity != null && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Specificity</Typography>
                        <Typography variant="body2">
                          <strong>{Math.round((assistQuality.specificity || 0) * 100)}%</strong>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (higher = more targeted)
                          </Typography>
                        </Typography>
                      </Box>
                    )}
                    {assistQuality.fp_likelihood != null && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">FP likelihood</Typography>
                        <Typography variant="body2">
                          <strong style={{ color: (assistQuality.fp_likelihood || 0) > 0.5 ? '#d32f2f' : (assistQuality.fp_likelihood || 0) > 0.25 ? '#ed6c02' : '#2e7d32' }}>
                            {Math.round((assistQuality.fp_likelihood || 0) * 100)}%
                          </strong>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (lower = fewer false positives)
                          </Typography>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {(assistQuality.concerns?.length ?? 0) > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Concerns to review:</Typography>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {assistQuality.concerns?.map((c, idx) => (
                          <li key={idx}><Typography variant="caption">{c}</Typography></li>
                        ))}
                      </ul>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* Sample non-matching code from AI (Beat 2) — matching sample
                pre-populates the Pattern Tester below; this contrast helps
                the user confirm intent. */}
            {assistExamples?.non_matching && (
              <Grid item xs={12}>
                <Box sx={{ p: 1.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Example of code the pattern should NOT fire on (correct / exempt usage):
                  </Typography>
                  <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', m: 0, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                    {assistExamples.non_matching}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Live Pattern Tester */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Pattern Tester
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Lock sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Runs locally in your browser. Code is never sent to any server.
                    </Typography>
                  </Box>
                </Box>
                <TextField
                  fullWidth size="small" multiline rows={3}
                  placeholder="Paste a code snippet to test your pattern against..."
                  value={testCode}
                  onChange={e => { setTestCode(e.target.value); setTestMatches(null) }}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    size="small" variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={runPatternTest}
                    disabled={!form.pattern || !testCode}
                  >
                    Test Pattern
                  </Button>
                  {testMatches && (
                    <Chip
                      label={testMatches.matched ? `${testMatches.count} match${testMatches.count > 1 ? 'es' : ''}` : 'No matches'}
                      color={testMatches.matched ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                  {testMatches?.matched && testMatches.highlights.length > 0 && (
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'success.main' }}>
                      {testMatches.highlights.map((h, i) => `"${h}"`).join(', ')}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Dry-run against tenant's recent scans */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.light' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Test against my last 5 scans</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Replays your pattern against your recent scan history. No findings are persisted.
                  </Typography>
                </Box>
                <Button
                  size="small" variant="contained"
                  startIcon={dryRunning ? <CircularProgress size={14} /> : <PlayArrow />}
                  onClick={runDryRunAgainstScans}
                  disabled={dryRunning || !form.pattern}
                  sx={{ mb: dryRunResult ? 1 : 0 }}
                >
                  {dryRunning ? 'Replaying...' : 'Run dry-run'}
                </Button>
                {dryRunResult && (
                  <Box>
                    <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                      Would fire on <strong>{dryRunResult.would_fire_on}</strong> agent{dryRunResult.would_fire_on === 1 ? '' : 's'} across{' '}
                      <strong>{dryRunResult.total_scans_evaluated}</strong> scan{dryRunResult.total_scans_evaluated === 1 ? '' : 's'}{' '}
                      ({dryRunResult.total_agents_evaluated} agent{dryRunResult.total_agents_evaluated === 1 ? '' : 's'} evaluated).
                    </Typography>
                    {dryRunResult.would_fire_on === 0 && dryRunResult.total_agents_evaluated > 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        No matches. Either your pattern is too narrow, or none of your recent agents
                        violate this rule. Tighten the pattern or save the rule and rescan to confirm.
                      </Alert>
                    )}
                    {dryRunResult.total_agents_evaluated === 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        No agents in your recent scans to evaluate against. Run a scan first.
                      </Alert>
                    )}
                    {dryRunResult.sample_matches.length > 0 && (
                      <Box sx={{ maxHeight: 200, overflowY: 'auto', bgcolor: 'background.paper', p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Sample matches:</Typography>
                        {dryRunResult.sample_matches.slice(0, 10).map((m, idx) => (
                          <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}>
                            <Chip label={m.framework || 'general'} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {m.agent_name} <span style={{ color: '#888' }}>· {m.file_path}</span>
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Step 3: Remediation */}
            <Grid item xs={12}>
              <TextField fullWidth label="Remediation Guidance" multiline rows={2}
                placeholder="What should the developer do to fix this?"
                value={form.remediation} onChange={e => setForm({ ...form, remediation: e.target.value })} />
            </Grid>

            {/* Step 4: Auto-generated name */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField fullWidth label="Rule Name" required
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  helperText="Auto-generated from your selections, or edit manually" />
                <Button
                  variant="outlined" size="small" sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  onClick={generateAutoName}
                  disabled={!form.description}
                >
                  Auto Name
                </Button>
              </Box>
            </Grid>

            {/* Lifecycle + propose-to-platform (Beat 11) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Lifecycle</InputLabel>
                <Select
                  value={form.lifecycle_state}
                  label="Lifecycle"
                  onChange={e => setForm({ ...form, lifecycle_state: e.target.value })}
                >
                  {LIFECYCLE_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.propose_to_platform}
                    onChange={e => setForm({ ...form, propose_to_platform: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Share with ARIAS for review</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ARIAS reviews your rule and may adopt it as a default for all tenants if broadly useful — only the rule pattern + remediation is shared, not your scan data. Turn off to keep this rule private to your tenant.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5 }}
              />
            </Grid>

            {hasFrameworks && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Frameworks (optional)</InputLabel>
                  <Select
                    multiple
                    label="Frameworks (optional)"
                    value={selectedFrameworks}
                    onChange={(e) => setSelectedFrameworks(
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : (e.target.value as string[]),
                    )}
                    renderValue={(selected) => (selected as string[])
                      .map((k) => frameworkOptions.find((f) => f.key === k)?.display_name || k)
                      .join(', ')}
                  >
                    {frameworkOptions.map((f) => (
                      <MenuItem key={f.key} value={f.key}>{f.display_name}</MenuItem>
                    ))}
                    {!frameworkOptions.length && (
                      <MenuItem disabled value="">
                        No frameworks yet — create one on the Frameworks tab
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText component="div">
                    A rule can belong to several frameworks; it stays one rule. Adding it
                    to a framework creates a control there whose wording is this rule's
                    own — the rule is the obligation.
                    <Box sx={{ mt: 0.5 }}>
                      That control draws on findings in this rule's category, from every
                      agent ARIAS has scanned, not only on what this rule produces. Set a
                      framework to "its own rules only" if you want it judged more
                      narrowly.
                    </Box>
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {/* Audit trail footer (Beat 10) — only on edit */}
            {editingRule && (editingRule.created_by || editingRule.updated_by || editingRule.created_at) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  {editingRule.created_by ? `Created by ${editingRule.created_by}` : 'Created'}
                  {editingRule.created_at ? ` on ${new Date(editingRule.created_at).toLocaleDateString()}` : ''}
                  {editingRule.updated_by && editingRule.updated_by !== editingRule.created_by
                    ? ` · Last updated by ${editingRule.updated_by}`
                    : ''}
                  {editingRule.updated_at && editingRule.updated_at !== editingRule.created_at
                    ? ` on ${new Date(editingRule.updated_at).toLocaleDateString()}`
                    : ''}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDialogOpen(false)
            setTestCode(''); setTestMatches(null); setDryRunResult(null)
            setSimilarRules([]); setGroundedFrameworks([])
            setAssistExamples(null); setAssistExplanation('')
            setAssistVariants(null); setAssistQuality(null); setActiveVariant('balanced')
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name || !form.pattern}>
            {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      {projections.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setProjections([])}>
          <AlertTitle>What this rule's control will draw on</AlertTitle>
          {projections.map((note, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{note}</Typography>
          ))}
        </Alert>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Template Picker Dialog */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Start from a Template
          <Typography variant="body2" color="text.secondary">
            Pick a common policy as a starting point. You can edit anything before saving.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Category filter chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            <Chip
              label={`All (${templates.length})`}
              size="small"
              color={activeCategory === 'all' ? 'primary' : 'default'}
              variant={activeCategory === 'all' ? 'filled' : 'outlined'}
              onClick={() => setActiveCategory('all')}
            />
            {templateCategories.map((cat) => {
              const count = templates.filter(t => t.category === cat.id).length
              if (count === 0) return null
              return (
                <Chip
                  key={cat.id}
                  label={`${cat.label} (${count})`}
                  size="small"
                  color={activeCategory === cat.id ? 'primary' : 'default'}
                  variant={activeCategory === cat.id ? 'filled' : 'outlined'}
                  onClick={() => setActiveCategory(cat.id)}
                />
              )
            })}
          </Box>

          {templates.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {templates
                .filter(t => activeCategory === 'all' || t.category === activeCategory)
                .map((tmpl) => (
                  <Grid item xs={12} sm={6} key={tmpl.id}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={tmpl.severity}
                            size="small"
                            color={getSeverityColor(tmpl.severity) as any}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Chip
                            label={DIMENSIONS.find(d => d.value === tmpl.dimension)?.label || tmpl.dimension}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          {tmpl.framework && tmpl.framework !== 'general' && (
                            <Chip
                              label={tmpl.framework}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="subtitle2" gutterBottom>{tmpl.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {tmpl.description}
                        </Typography>
                        {tmpl.rationale && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                            Why: {tmpl.rationale}
                          </Typography>
                        )}
                      </CardContent>
                      <DialogActions sx={{ px: 2, pb: 1, pt: 0 }}>
                        <Button size="small" onClick={() => handlePickTemplate(tmpl)}>
                          Use this template
                        </Button>
                      </DialogActions>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  )
}

export default CustomRulesPage
